/**
 * Tier gating (pedagogical invariant 4, SPEC §5.1). **[DoD-3]**
 *
 * > Tier gating is on measured rolling accuracy over the last N attempts at that tier, **never on
 * > items seen, sessions played, or time spent.**
 *
 * There is deliberately no parameter in this module for volume, streaks, days, or sessions. The
 * only inputs are the qualifying attempts and the previous unlock state. A tier cannot be unlocked
 * by grinding, and grinding badly re-locks it — which is the point: the ladder is a measurement,
 * not a reward.
 *
 * WHAT THIS DOES NOT GATE: reviews. Invariant 7 outranks this file. Re-locking a tier never
 * removes an item already in circulation from the due queue (`queue.playableQueue` has no unlock
 * parameter, by construction). Gating governs what is *introduced*, never what is *due*.
 *
 * Pure. No clock, no randomness, no I/O.
 */

import type { Attempt } from '../store/schema';
import type { Tier } from '../state/gamestate';
import { advancesInterval } from './gate';

export interface GatingConfig {
  /** Attempts at that tier, most recent first. */
  n: number;
  /** Rolling accuracy at or above this unlocks the next tier. */
  openAt: number;
  /** Rolling accuracy below this re-locks it. */
  closeAt: number;
  /** Below this many qualifying attempts the tier is neither opened nor closed. */
  minSample: number;
}

/**
 * SPEC §5.1. `openAt > closeAt` is hysteresis: without the gap, an accuracy hovering at the
 * threshold would flap the tier open and shut between runs, which reads as the app changing its
 * mind about the learner rather than as a measurement.
 */
export const DEFAULT_GATING: GatingConfig = Object.freeze({
  n: 20,
  openAt: 0.85,
  closeAt: 0.75,
  minSample: 12,
});

export const ALL_TIERS: readonly Tier[] = [1, 2, 3, 4, 5, 6];

export type UnlockMap = Record<Tier, boolean>;

/**
 * Tiers 1 and 2 are open from the first launch. There is nothing below them to measure, and an
 * app that opens with everything locked has made the learner audition for the right to start.
 *
 * Tier 1 being open is not the same as tier 1 being *available*: it is speech-only and is filtered
 * out entirely where the microphone is not (SPEC §5, `queue.playableQueue`). Those are different
 * questions and are answered in different places on purpose.
 */
export function baseUnlockState(): UnlockMap {
  return { 1: true, 2: true, 3: false, 4: false, 5: false, 6: false };
}

/**
 * Attempts that count toward rolling accuracy: production-mode, unhinted, first attempt on that
 * item in that run (SPEC §5.1).
 *
 * Exactly the same predicate that decides whether an attempt advances an FSRS interval, and
 * deliberately imported rather than restated — if the two definitions of "a real attempt" could
 * drift, one of them would eventually be wrong.
 */
export function isQualifying(attempt: Attempt): boolean {
  return advancesInterval(attempt);
}

export interface AccuracyWindow {
  /** Qualifying attempts considered, up to `n`. */
  sample: number;
  /** Null when there is nothing to measure. Never 0 — those are different claims. */
  accuracy: number | null;
}

/**
 * Rolling accuracy over the last `n` qualifying attempts at one tier.
 *
 * Sorted here rather than trusting the caller: the attempt log is appended to from a live run and
 * read back from storage, and a window computed over the wrong twenty attempts would be a wrong
 * measurement that never throws.
 */
export function rollingAccuracy(
  attempts: readonly Attempt[],
  tier: Tier,
  cfg: GatingConfig = DEFAULT_GATING,
): AccuracyWindow {
  const window = attempts
    .filter((a) => a.tier === tier && isQualifying(a))
    .sort((a, b) => (b.at !== a.at ? b.at - a.at : (a.id < b.id ? 1 : a.id > b.id ? -1 : 0)))
    .slice(0, cfg.n);

  if (window.length === 0) return { sample: 0, accuracy: null };
  const correct = window.reduce((sum, a) => sum + (a.correct ? 1 : 0), 0);
  return { sample: window.length, accuracy: correct / window.length };
}

/** Why a tier is where it is. Rendered in the tracker drilldown; never as a judgement. */
export type GateReason =
  /** Open from first launch. */
  | 'base'
  /** Rolling accuracy at the tier below reached `openAt`. */
  | 'opened'
  /** Rolling accuracy at the tier below fell under `closeAt`. */
  | 'closed'
  /** Between the thresholds: hysteresis holds whatever it was. */
  | 'held'
  /** Fewer than `minSample` qualifying attempts below. Neither opened nor closed. */
  | 'insufficient-sample'
  /** The tier below is locked, so this one cannot be open. */
  | 'blocked-by-predecessor';

export interface TierGate {
  tier: Tier;
  unlocked: boolean;
  reason: GateReason;
  /** The measurement this decision was made from: the tier *below*. Null for the base tiers. */
  measuredTier: Tier | null;
  sample: number;
  accuracy: number | null;
}

export type GateEvaluation = Record<Tier, TierGate>;

/**
 * The next unlock state, from the previous one and the attempt log.
 *
 * Takes the previous state because hysteresis is stateful: between `closeAt` and `openAt` the
 * answer is "whatever it already was". A stateless recomputation would silently pick one side of
 * the band and flap.
 */
export function evaluateGates(
  previous: UnlockMap,
  attempts: readonly Attempt[],
  cfg: GatingConfig = DEFAULT_GATING,
): GateEvaluation {
  const next = {} as GateEvaluation;

  next[1] = { tier: 1, unlocked: true, reason: 'base', measuredTier: null, sample: 0, accuracy: null };
  next[2] = { tier: 2, unlocked: true, reason: 'base', measuredTier: null, sample: 0, accuracy: null };

  for (const tier of [3, 4, 5, 6] as Tier[]) {
    const below = (tier - 1) as Tier;
    const { sample, accuracy } = rollingAccuracy(attempts, below, cfg);

    let unlocked: boolean;
    let reason: GateReason;

    if (sample < cfg.minSample || accuracy === null) {
      // Not enough evidence to move it either way. Note what this rules out: a tier can never open
      // because a lot of attempts happened, only because enough *accurate* ones did.
      unlocked = previous[tier];
      reason = 'insufficient-sample';
    } else if (accuracy >= cfg.openAt) {
      unlocked = true;
      reason = 'opened';
    } else if (accuracy < cfg.closeAt) {
      unlocked = false;
      reason = 'closed';
    } else {
      unlocked = previous[tier];
      reason = 'held';
    }

    // No skipping. Tier 5 cannot be open while tier 4 is shut, whatever tier 4's accuracy says,
    // because the measurement that would open 5 is made at 4 and there would be none.
    if (unlocked && !next[below].unlocked) {
      unlocked = false;
      reason = 'blocked-by-predecessor';
    }

    next[tier] = { tier, unlocked, reason, measuredTier: below, sample, accuracy };
  }

  return next;
}

/** Just the booleans, for callers that do not need the diagnostics. */
export function toUnlockMap(evaluation: GateEvaluation): UnlockMap {
  return {
    1: evaluation[1].unlocked,
    2: evaluation[2].unlocked,
    3: evaluation[3].unlocked,
    4: evaluation[4].unlocked,
    5: evaluation[5].unlocked,
    6: evaluation[6].unlocked,
  };
}

/** Tiers that may have new items introduced right now. */
export function unlockedTiers(unlocked: UnlockMap): Tier[] {
  return ALL_TIERS.filter((tier) => unlocked[tier]);
}
